import React, { useState, useEffect } from 'react';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import { formatRoleName } from '../utils/roleUtils';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api, { getRoles } from '../services/api';

interface Role {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    tenantId?: string | null;
    permissions: Array<{ id: string; name: string }>;
  };
  isSystemRole?: boolean; // Flag for system roles (not in database)
}

interface RoleManagementProps {
  skipLayout?: boolean;
}

const RoleManagement: React.FC<RoleManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Array<{ _id: string; metadata: { name: string }; spec: { permStr: string } }>>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    selectedPermissions: [] as Array<{ id: string; name: string }>,
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ name?: string; permissions?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  // Permission categories structure - categories are resources (booking, patient, etc.)
  interface PermissionCategory {
    id: string; // resource name (e.g., "booking")
    name: string; // capitalized resource name (e.g., "Booking")
    operations: {
      [operation: string]: string[]; // operation name -> array of permission IDs
    };
  }

  // Map permissions to categories based on resource type
  const getPermissionCategories = (): PermissionCategory[] => {
    const categoryMap: { [key: string]: PermissionCategory } = {};
    
    permissions.forEach((perm) => {
      const permStr = perm.spec.permStr;
      const [resource, operation] = permStr.split('.');
      
      if (!categoryMap[resource]) {
        let categoryDisplayName = (perm.metadata?.name || '').trim();
        if (categoryDisplayName) {
          // Map permStr operation to display suffix (metadata.name uses "Check In" not "Checkin", etc.)
          const opToDisplaySuffix: Record<string, string> = {
            checkin: 'Check In',
            checkout: 'Check Out',
            lifecycle: 'Trip Lifecycle',
          };
          const opSuffix = opToDisplaySuffix[operation.toLowerCase()] ?? (operation.charAt(0).toUpperCase() + operation.slice(1));
          const escaped = opSuffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          categoryDisplayName = categoryDisplayName.replace(new RegExp('\\s*' + escaped + '\\s*$', 'i'), '').trim();
        }
        if (!categoryDisplayName) {
          categoryDisplayName = resource.charAt(0).toUpperCase() + resource.slice(1);
        }
        categoryDisplayName = categoryDisplayName.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
        categoryMap[resource] = {
          id: resource,
          name: categoryDisplayName,
          operations: {},
        };
      }
      
      if (!categoryMap[resource].operations[operation]) {
        categoryMap[resource].operations[operation] = [];
      }
      
      categoryMap[resource].operations[operation].push(perm._id);
    });
    
    return Object.values(categoryMap)
      .filter((c) => c.name != 'Permission' && c.name != 'Tenant')
      .sort((a, b) => a.name.localeCompare(b.name));
  };
  
  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    const categories = getPermissionCategories();
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return;
    
    // When checking a category, enable all operations
    // When unchecking, remove all permissions
    const allPermIds: string[] = [];
    Object.values(category.operations).forEach((permIds) => {
      allPermIds.push(...permIds);
    });
    
    setFormData((prev) => {
      if (checked) {
        const newPerms = allPermIds
          .filter((id) => !prev.selectedPermissions.some((p) => p.id === id))
          .map((id) => {
            const perm = permissions.find((p) => p._id === id);
            return { id, name: perm?.spec.permStr || '' };
          });
        return {
          ...prev,
          selectedPermissions: [...prev.selectedPermissions, ...newPerms],
        };
      } else {
        return {
          ...prev,
          selectedPermissions: prev.selectedPermissions.filter(
            (p) => !allPermIds.includes(p.id)
          ),
        };
      }
    });
  };
  
  const handleOperationToggle = (
    categoryId: string,
    operation: string,
    checked: boolean
  ) => {
    const categories = getPermissionCategories();
    const category = categories.find((c) => c.id === categoryId);
    if (!category || !category.operations[operation]) return;
    
    const permIds = category.operations[operation] || [];
    
    setFormData((prev) => {
      if (checked) {
        const newPerms = permIds
          .filter((id) => !prev.selectedPermissions.some((p) => p.id === id))
          .map((id) => {
            const perm = permissions.find((p) => p._id === id);
            return { id, name: perm?.spec.permStr || '' };
          });
        return {
          ...prev,
          selectedPermissions: [...prev.selectedPermissions, ...newPerms],
        };
      } else {
        return {
          ...prev,
          selectedPermissions: prev.selectedPermissions.filter(
            (p) => !permIds.includes(p.id)
          ),
        };
      }
    });
  };
  
  const isCategoryChecked = (categoryId: string): boolean => {
    const categories = getPermissionCategories();
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return false;
    
    // Category is checked if at least one operation is enabled
    // Unchecked only when all operations are unchecked
    const allPermIds: string[] = [];
    Object.values(category.operations).forEach((permIds) => {
      allPermIds.push(...permIds);
    });
    
    // Return true if at least one permission is selected
    return allPermIds.some((id) =>
      formData.selectedPermissions.some((p) => p.id === id)
    );
  };
  
  const isOperationChecked = (
    categoryId: string,
    operation: string
  ): boolean => {
    const categories = getPermissionCategories();
    const category = categories.find((c) => c.id === categoryId);
    if (!category || !category.operations[operation]) return false;
    
    const permIds = category.operations[operation] || [];
    return permIds.length > 0 && permIds.every((id) =>
      formData.selectedPermissions.some((p) => p.id === id)
    );
  };
  
  // Get color for operation toggle - each operation type has its own unique color
  const getOperationColor = (operation: string): string => {
    const colorMap: { [key: string]: string } = {
      'get': '#4caf50',        // green
      'list': '#2196f3',       // blue
      'create': '#ffc107',     // yellow/amber
      'update': '#ff9800',     // orange
      'delete': '#f44336',     // red
      'view': '#9c27b0',       // purple
      'read': '#00bcd4',       // cyan
      'checkin': '#8bc34a',    // light green
      'checkout': '#ff5722',   // deep orange
      'livemap': '#3f51b5',    // indigo
      'lifecycle': '#009688',  // teal
    };
    
    // Return the color for the operation, or a default color if not found
    return colorMap[operation.toLowerCase()] || '#607d8b'; // default: blue grey
  };
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  const fetchPermissions = async () => {
    try {
      // Fetch all permissions (paginate to avoid hard limits) and sort alphabetically
      const pageSize = 500;
      let page = 1;
      let allPerms: any[] = [];
      while (true) {
        const response = await api.get('/permissions', {
          params: { page, pageSize },
        });
        const batch = response.data.data || [];
        allPerms = allPerms.concat(batch);
        if (!response.data.pagination || batch.length < pageSize) break;
        page += 1;
      }

      allPerms.sort((a: any, b: any) =>
        (a.metadata?.name || '').localeCompare(b.metadata?.name || '', undefined, { sensitivity: 'base' })
      );
      setPermissions(allPerms);
    } catch (err: any) {
      console.error('Failed to load permissions:', err);
    }
  };

  const fetchRoles = async (pageOverride?: number, pageSizeOverride?: number) => {
    try {
      setLoading(true);
      const pageToUse = pageOverride ?? currentPage;
      const sizeToUse = pageSizeOverride ?? pageSize;
      const response = await getRoles(pageToUse, sizeToUse, searchTerm || undefined);
      setRoles(response.data.data || []);
      const pagination = response.data.pagination;
      if (pagination) {
        setCurrentPage(pagination.currentPage || pageToUse);
        setPageSize(pagination.pageSize || sizeToUse);
        setTotalItems(pagination.totalItems || 0);
        setHasNextPage(pagination.hasNextPage || false);
        setHasPreviousPage(pagination.hasPreviousPage || false);
      }
    } catch (err: any) {
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role?: Role) => {
    // Prevent editing system roles
    if (role && (role.isSystemRole || role._id.startsWith('system-'))) {
      setError('System roles cannot be edited. They are built-in and managed by the system.');
      return;
    }
    
    if (role) {
      setEditingRole(role);
      setFormData({ 
        name: role.metadata.name,
        selectedPermissions: role.spec.permissions || [],
      });
    } else {
      setEditingRole(null);
      setFormData({ 
        name: '',
        selectedPermissions: [],
      });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
  };

  const handlePermissionToggle = (permId: string, permName: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedPermissions.some(p => p.id === permId);
      if (isSelected) {
        return {
          ...prev,
          selectedPermissions: prev.selectedPermissions.filter(p => p.id !== permId)
        };
      } else {
        return {
          ...prev,
          selectedPermissions: [...prev.selectedPermissions, { id: permId, name: permName }]
        };
      }
    });
  };

  const handleToggleSelectAllPermissions = () => {
    const allSelected = permissions.every((perm) =>
      formData.selectedPermissions.some((p) => p.id === perm._id)
    );

    if (allSelected) {
      setFormData((prev) => ({
        ...prev,
        selectedPermissions: [],
      }));
    } else {
      const all = permissions.map((perm) => ({
        id: perm._id,
        name: perm.spec.permStr,
      }));
      setFormData((prev) => ({
        ...prev,
        selectedPermissions: all,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    const newValidationErrors: { name?: string; permissions?: string } = {};
    if (!formData.name.trim()) {
      newValidationErrors.name = 'Role name is required';
    }
    if (formData.selectedPermissions.length === 0) {
      newValidationErrors.permissions = 'At least one permission is required';
    }

    if (Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    try {
      const data = {
        metadata: { name: formData.name },
        spec: { permissions: formData.selectedPermissions },
      };

      if (editingRole) {
        await api.put(`/roles/${editingRole._id}`, data);
      } else {
        await api.post('/roles', data);
      }

      setShowModal(false);
      fetchRoles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    // Prevent deleting system roles
    if (id.startsWith('system-')) {
      setErrorMessage('System roles cannot be deleted. They are built-in and managed by the system.');
      setShowErrorModal(true);
      return;
    }
    
    setRoleToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchRoles();
  };

  const handlePageChange = (page: number) => {
    fetchRoles(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    fetchRoles(1, size);
  };

  const filteredRoles = roles;

  const confirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      await api.delete(`/roles/${roleToDelete.id}`);
      fetchRoles();
      setShowDeleteConfirm(false);
      setRoleToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete role. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setRoleToDelete(null);
      setShowErrorModal(true);
    }
  };

  const content = (
    <>
      {/* <div className="page-header">
        <h2>Role Management</h2>
        <p>Manage roles and their permissions</p>
      </div> */}

      <div className="card">
        <div className="card-header">
          {/* <h3 className="card-title">Roles</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search roles by name..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('role.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Role
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading roles...</div>
        ) : roles.length === 0 ? (
          <div className="empty-state">
            <h3>No roles yet</h3>
            <p>Create your first role to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
          <div className="table-container" style={{ height: 'calc(100vh - 450px)', overflow: 'auto' }}>
            <table className="bookings-table">
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th>Name</th>
                  <th>Scope</th>
                  <th>Permissions</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map((role) => {
                  const isSystemRole = role.isSystemRole || role._id.startsWith('system-');
                  const isSystemLevel = !role.spec.tenantId;
                  
                  return (
                    <tr key={role._id}>
                      <td>
                        {formatRoleName(role.metadata.name)}
                        {isSystemRole && (
                          <span style={{ 
                            marginLeft: '8px', 
                            fontSize: '13px', 
                            color: '#9c27b0',
                            fontWeight: 'bold'
                          }}>
                            🔒 SYSTEM
                          </span>
                        )}
                        {!isSystemRole && isSystemLevel && (
                          <span style={{ 
                            marginLeft: '8px', 
                            fontSize: '13px', 
                            color: '#667eea',
                            fontWeight: 'bold'
                          }}>
                            🌍 GLOBAL
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: '500',
                          background: isSystemRole ? '#f3e5f5' : (isSystemLevel ? '#e8f5e9' : '#fff3cd'),
                          color: isSystemRole ? '#7b1fa2' : (isSystemLevel ? '#2e7d32' : '#856404'),
                        }}>
                          {isSystemRole ? 'System Role' : (isSystemLevel ? 'System-Level' : 'Tenant-Specific')}
                        </span>
                      </td>
                      <td>{role.spec.permissions.length} permissions</td>
                      <td>
                        {isSystemRole ? (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>Built-in</span>
                        ) : (
                          formatDateDDMMYYYY(role.metadata.createdAt)
                        )}
                      </td>
                      <td>
                        {isSystemRole ? (
                          <span style={{ 
                            color: '#999', 
                            fontSize: '14px',
                            fontStyle: 'italic'
                          }}>
                            Read-only
                          </span>
                        ) : !isSystemLevel ? (
                          <div className="action-buttons">
                            {hasPermission('role.update') && (
                              <button 
                                className="btn-icon btn-edit" 
                                onClick={() => handleOpenModal(role)}
                              >
                                Edit
                              </button>
                            )}
                            {hasPermission('role.delete') && (
                              <button 
                                className="btn-icon btn-delete" 
                                onClick={() => handleDelete(role._id, role.metadata.name)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && handlePageChange(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column' }}>
                {error && <div className="error-message">{error}</div>}

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>
                    Role Name
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={validationErrors.name ? 'input-error' : ''}
                    placeholder="e.g., Senior Doctor, Receptionist"
                  />
                  {validationErrors.name && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.name}
                    </small>
                  )}
                </div>

                <div
                  className="form-group"
                  style={{ marginBottom: '0', flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <label>
                    Permissions
                    <span className="required-star">*</span>
                    <span style={{ fontSize: '1.3em', marginLeft: '4px' }}>
                      ({formData.selectedPermissions.length} selected)
                    </span>
                  </label>
                  {permissions.length === 0 ? (
                    <p style={{ 
                      color: isDark ? '#999' : '#666', 
                      fontSize: '16px', 
                      fontStyle: 'italic' 
                    }}>
                      No permissions available. System admin should create permissions first.
                    </p>
                  ) : (
                    <div
                      className={`permission-list-wrapper ${
                        validationErrors.permissions ? 'input-error' : ''
                      }`}
                      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                    >
                      <div
                        className="permission-list"
                        style={{
                          flex: 1,
                          overflowY: 'auto',
                          maxHeight: error ? '520px' : '625px',
                          padding: '8px',
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
                        }}
                      >
                        {getPermissionCategories().map((category) => {
                          const isCategorySelected = isCategoryChecked(category.id);
                          const operations = Object.keys(category.operations).sort();

                          return (
                            <div
                              key={category.id}
                              style={{
                                marginBottom: '12px',
                                border: isDark ? '1px solid #444' : '1px solid #e0e0e0',
                                borderRadius: '6px',
                                padding: '12px',
                                background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  marginBottom: '12px',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isCategorySelected}
                                  onChange={(e) => handleCategoryToggle(category.id, e.target.checked)}
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    accentColor: '#2196f3',
                                  }}
                                />
                                <div
                                  style={{
                                    fontWeight: '700',
                                    fontSize: '18px',
                                    color: isDark ? '#e0e0e0' : '#333',
                                  }}
                                >
                                  {category.name}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '32px',
                                  flexWrap: 'wrap',
                                  marginLeft: '30px',
                                }}
                              >
                                {operations.map((operation) => {
                                  const isOpChecked = isOperationChecked(category.id, operation);
                                  const opColor = getOperationColor(operation);
                                  const operationLabel = operation.charAt(0).toUpperCase() + operation.slice(1);
                                  
                                  return (
                                    <div
                                      key={operation}
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px',
                                        cursor: 'pointer',
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleOperationToggle(category.id, operation, !isOpChecked);
                                      }}
                                    >
                                      <span style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '500',
                                        color: isDark ? '#e0e0e0' : '#333',
                                      }}>
                                        {operationLabel}
                                      </span>
                                      <div
                                        style={{
                                          width: '44px',
                                          height: '19px',
                                          borderRadius: '10px',
                                          background: isOpChecked ? opColor : (isDark ? '#555' : '#ccc'),
                                          position: 'relative',
                                          transition: 'background 0.3s',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: '17px',
                                            height: '17px',
                                            borderRadius: '50%',
                                            background: isDark ? '#e0e0e0' : '#fff',
                                            position: 'absolute',
                                            top: '1px',
                                            left: isOpChecked ? '26px' : '1px',
                                            transition: 'left 0.3s',
                                            boxShadow: isDark ? '0 2px 4px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.2)',
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <small style={{ 
                    color: isDark ? '#999' : '#666', 
                    fontSize: '14px', 
                    marginTop: '8px', 
                    display: 'block' 
                  }}>
                    Select categories and toggle operations (get, list, create, update, delete, etc.)
                  </small>
                  {validationErrors.permissions && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.permissions}
                    </small>
                  )}
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingRole ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingRole ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${roleToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setRoleToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default RoleManagement;

